import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMyTasks, patchTask, deleteTaskApi, getTeam, getMe, canEdit, type TaskInput } from '../lib/api.js';
import { Card, PageHeader, Spinner, StatTile } from '../components/ui.js';
import { TaskItem } from '../components/Tasks.js';

/**
 * Cross-deal task dashboard. "Mine" = tasks assigned to me OR on a deal I own (the deal's poc);
 * "All" = every deal's tasks. Rows are the same TaskItem used on the per-deal Tasks tab, with a
 * deal chip that links back. Edits/deletes go through the same API and invalidate every My Tasks view.
 */
export function MyTasks() {
  const qc = useQueryClient();
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [showDone, setShowDone] = useState(false);

  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 300_000 });
  const teamQ = useQuery({ queryKey: ['team'], queryFn: getTeam });
  const tasksQ = useQuery({ queryKey: ['myTasks', scope, showDone], queryFn: () => getMyTasks({ scope, done: showDone }) });

  const team = (teamQ.data?.team ?? []).filter((m) => m.active);
  const editable = canEdit(meQ.data?.identity);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['myTasks'] });
  const patchM = useMutation({ mutationFn: ({ id, patch }: { id: string; patch: TaskInput }) => patchTask(id, patch), onSuccess: invalidate });
  const delM = useMutation({ mutationFn: (id: string) => deleteTaskApi(id), onSuccess: invalidate });
  const busy = patchM.isPending || delM.isPending;

  const tasks = tasksQ.data?.tasks ?? [];
  const midnight = new Date(new Date().toDateString());
  const open = tasks.filter((t) => t.status !== 'done').length;
  const overdue = tasks.filter((t) => t.dueDate && t.status !== 'done' && new Date(t.dueDate) < midnight).length;

  const seg = (active: boolean) =>
    `px-3 py-1.5 text-[12px] font-semibold rounded-md ${active ? 'bg-navy text-white' : 'text-muted hover:text-navy'}`;

  return (
    <div>
      <PageHeader
        eyebrow="Workspace"
        title="My Tasks"
        desc={scope === 'mine' ? 'To-dos and reviews assigned to you, plus tasks on deals you own.' : 'Every task across all deals.'}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5 bg-canvas border border-line rounded-lg p-0.5">
              <button type="button" className={seg(scope === 'mine')} onClick={() => setScope('mine')}>Mine</button>
              <button type="button" className={seg(scope === 'all')} onClick={() => setScope('all')}>All</button>
            </div>
            <label className="flex items-center gap-1.5 text-[12px] text-muted cursor-pointer">
              <input type="checkbox" className="h-3.5 w-3.5 accent-teal" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
              Show done
            </label>
          </div>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatTile label="Open" value={open} accent="teal" />
        <StatTile label="Overdue" value={overdue} accent={overdue ? 'red' : 'green'} note={overdue ? 'past due date' : 'nothing overdue'} />
        <StatTile label="Showing" value={tasks.length} accent="navy" note={scope === 'mine' ? 'mine' : 'all deals'} />
      </div>

      <Card title={scope === 'mine' ? 'Assigned to me / my deals' : 'All tasks'} sub={showDone ? 'including done' : 'open only'}>
        {tasksQ.isLoading ? (
          <div className="flex items-center gap-2 text-muted text-[12.5px] py-4"><Spinner size={14} /> Loading tasks…</div>
        ) : tasksQ.error ? (
          <p className="text-[12.5px] text-red py-3">{String(tasksQ.error as Error)}</p>
        ) : tasks.length === 0 ? (
          <div className="text-center text-muted text-[12.5px] py-8">
            {scope === 'mine' ? 'Nothing assigned to you' : 'No tasks yet'}{showDone ? '.' : ' — you’re all caught up. 🎉'}
          </div>
        ) : (
          tasks.map((t) => (
            <TaskItem key={t.id} task={t} team={team} editable={editable} showDeal busy={busy}
              onPatch={(id, patch) => patchM.mutate({ id, patch })} onDelete={(id) => delM.mutate(id)} />
          ))
        )}
      </Card>
    </div>
  );
}
